// token.interceptor.ts

import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { omitAutherisation, QTED_URL } from '../../auth/config/constants';
import { IGETRefreshToken, IRefreshToken } from '../../auth/models/IUser';
import { AuthService } from '../../auth/services/auth.service';
import { StorageService } from '../helpers/storage.service';
import { Storagekeys } from '../model/storage';
import { LogLevel, LoggerService } from '../services/logger.service';

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  // Clone the request and add the authorization header
  // const isOmitted=
  const storageService = inject(StorageService);
  const loggerService = inject(LoggerService);
  const authService = inject(AuthService);
  const snackBar = inject(MatSnackBar);
  const isNoAuthEndpoint = omitAutherisation.some(url => {
    return req.url.includes(url);
  });

  // If the request is to a no-auth endpoint, pass the request as-is
  if (isNoAuthEndpoint) {
    return next(req).pipe(
      catchError((err: HttpErrorResponse) => {
        handlerError(err);
        // Re-throw the error to propagate it further
        return throwError(() => err);
      })
    );
  }
  const acctoken = storageService.getItem(Storagekeys.token, true);
  const accSessionId = storageService.getItem(Storagekeys.sessionId, true);
  const authReq = getHeaders(req);

  let isRefreshing = false;
  const refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Pass the cloned request with the updated header to the next handler
  function handlerError(err: HttpErrorResponse) {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 500) {
        const message =
          err?.error ??
          'Something went wrong, unable to process your request, check your request or please try after sometime.';
        // Handle other HTTP error codes
        snackBar.open(message, 'close', {
          duration: 5000,
          panelClass: 'errorSnack',
        });
        loggerService.log(LogLevel.ERROR, err);
      } else {
        // Handle other HTTP error codes
        loggerService.log(LogLevel.ERROR, err);
      }
    } else {
      // Handle non-HTTP errors
      loggerService.log(LogLevel.ERROR, err);
    }
  }
  function handle401Error(req: HttpRequest<unknown>, next: HttpHandlerFn) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);
      const refreshToken = storageService.getItem(
        Storagekeys.refreshToken,
        true
      );

      if (!refreshToken) {
        authService.logoutEvent.emit(
          'Auto Logout From Tenant Due To Lack Of Refresh Token'
        );
        return throwError(() => new Error('No refresh token available'));
      }
      return authService.refreshToken().pipe(
        switchMap((authDetails: IRefreshToken) => {
          storageService.storeItem(Storagekeys.token, authDetails.token, true);
          storageService.storeItem(
            Storagekeys.refreshToken,
            authDetails.refreshToken,
            true
          );
          
          const newAuthReq = req.clone({
            headers: req.headers
              .set('Authorization', `Bearer ${authDetails.token}`)
              .set('X-Kunnect-Refresh', `XXXXXXXXXX`),
          });
          
          return next(newAuthReq);
        }),
        catchError(err => {
          handlerError(err);
          if (req.url.includes('RefreshToken')) {
            authService.logoutEvent.emit(
              'Auto Logout From Tenant Due To Refresh API Failed'
            );
          }
          return throwError(() => err);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    } else {
      // Wait for the refresh token process to complete
      return refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const newAuthReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          });
          return next(newAuthReq);
        })
      );
    }
  }
  function getRefreshToken(req: HttpRequest<unknown>, next: HttpHandlerFn) {
    return authService.getRefreshToken().pipe(
      switchMap((tokenDetails: IGETRefreshToken) => {
        storageService.storeItem(
          Storagekeys.refreshToken,
          tokenDetails.refreshToken,
          true
        );
        return handle401Error(authReq, next);
      }),
      catchError(err => {
        handlerError(err);
        if (req.url.includes('GetRefreshToken')) {
          authService.logoutEvent.emit(
            'Auto Logout From Tenant Due To GetRefreshToken API Failed'
          );
        }
        return throwError(() => err);
      }),
      finalize(() => {
        isRefreshing = false;
      })
    );
  }
  function getHeaders(req: HttpRequest<unknown>) {

    let authReq;
    if (req.body instanceof FormData) authReq = req.clone();
    else {
      let headers = {};
      if (
        !req.url.includes('api.zippopotam.us') &&
        !req.url.includes('amazonaws.com') &&
        !req.url.includes(QTED_URL.QTED_DOMAIN)
      ) {
        headers = {
          Authorization: `Bearer ${acctoken}`,
          'Content-Type': 'application/json; charset=utf-8',
          'X-Kunnect-Sid': `${accSessionId}`,
          'X-Service-id': String(storageService.getItem(Storagekeys.orgId, true)),
          Accept: 'application/json',
        };

      } else if (req.url.includes(QTED_URL.QTED_DOMAIN)) {
        headers = {
          token: `${acctoken}`,
          'Content-Type': 'application/json; charset=utf-8',
          Accept: 'application/json',
        };
      } else headers = {};

      authReq = req.clone({
        headers: new HttpHeaders({ ...headers }),
      });
      
    }
    return authReq;
  }
  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        if (req.url.includes('RefreshToken')) {
          authService.logoutEvent.emit(
            'Auto Logout From Tenant Due To Refresh API Failed With 401'
          );
          return throwError(() => err);
        }
        return getRefreshToken(authReq, next);
      } else {
        handlerError(err);
        return throwError(() => err);
      }
    })
  );
};
